const STORAGE_KEY = 'verdiflora_delivery_country';
const CART_ATTRIBUTE_NAME = 'Verdiflora país activo';

const COUNTRIES = {
  guatemala: { url: '/pages/guatemala', label: 'Guatemala', tag: 'Guatemala' },
  colombia: { url: '/pages/colombia', label: 'Colombia', tag: 'Colombia' },
  'el-salvador': { url: '/pages/el-salvador', label: 'El Salvador', tag: 'El Salvador' },
  panama: { url: '/pages/panama', label: 'Panamá', tag: 'Panamá' },
  mexico: { url: '/pages/mexico', label: 'México', tag: 'México' },
  argentina: { url: '/pages/argentina', label: 'Argentina', tag: 'Argentina' },
  'republica-dominicana': {
    url: '/pages/republica-dominicana',
    label: 'República Dominicana',
    tag: 'Rep. Dominicana',
  },
  chile: { url: '/pages/chile', label: 'Chile', tag: 'Chile' },
};

let countrySyncPromise = Promise.resolve();

function localizeStorePath(path) {
  if (!path) return '';

  // Si ya es una URL absoluta, se conserva tal cual.
  if (/^https?:\/\//i.test(path)) return path;

  const routesRoot = window.Shopify?.routes?.root || '/';
  const cleanPath = path.startsWith('/') ? path : `/${path}`;

  // En el idioma principal, no hay prefijo.
  if (routesRoot === '/') return cleanPath;

  const cleanRoot = routesRoot.endsWith('/')
    ? routesRoot.slice(0, -1)
    : routesRoot;

  // Evita duplicar /en, /fr, etc. si el path ya viniera localizado.
  if (cleanPath === cleanRoot || cleanPath.startsWith(`${cleanRoot}/`)) {
    return cleanPath;
  }

  return `${cleanRoot}${cleanPath}`;
}

function getCountryHomeUrl(country) {
  const path = COUNTRIES[country]?.url;
  return path ? localizeStorePath(path) : '';
}

function getRegionalRoutes(selector) {
  if (selector._vfRegionalRoutes !== undefined) {
    return selector._vfRegionalRoutes;
  }

  const source = selector.querySelector('[data-vf-regional-routes]');

  if (!source) {
    selector._vfRegionalRoutes = null;
    return null;
  }

  try {
    selector._vfRegionalRoutes = JSON.parse(source.textContent || '{}');
  } catch (error) {
    console.warn('[Verdiflora regional routes]', error);
    selector._vfRegionalRoutes = null;
  }

  return selector._vfRegionalRoutes;
}

function getRegionalDestination(selector, country) {
  const routes = getRegionalRoutes(selector);
  const path = routes?.[country];

  return path ? localizeStorePath(path) : '';
}

function readSavedCountry() {
  try {
    const country = window.localStorage.getItem(STORAGE_KEY);
    return COUNTRIES[country] ? country : '';
  } catch (_error) {
    return '';
  }
}

async function saveCountryToCart(country) {
  if (!COUNTRIES[country]) return;

  const routesRoot = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${routesRoot}cart/update.js`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      attributes: {
        [CART_ATTRIBUTE_NAME]: COUNTRIES[country].label,
      },
    }),
  });

  if (!response.ok) {
    throw new Error('No fue posible guardar el país para la búsqueda.');
  }
}

function saveCountry(country) {
  if (!COUNTRIES[country]) return Promise.resolve();

  try {
    window.localStorage.setItem(STORAGE_KEY, country);
  } catch (_error) {
    // Continúa funcionando aunque localStorage esté bloqueado.
  }

  countrySyncPromise = countrySyncPromise
  .catch(() => {
    // Permite continuar aunque una sincronización anterior haya fallado.
  })
  .then(() => saveCountryToCart(country))
  .catch((error) => {
    console.warn('[Verdiflora country sync]', error);
  });

  window.VerdifloraCountryReady = countrySyncPromise;
  updateSearchVisibility(country);

  return countrySyncPromise;
}

function getActiveCountry() {
  const selector = document.querySelector('verdiflora-country-selector');
  const pageCountry = selector?.dataset.pageCountry || '';

  if (pageCountry && COUNTRIES[pageCountry]) return pageCountry;
  return readSavedCountry();
}

function updateSearchVisibility(country) {
  const hasCountry = Boolean(country && COUNTRIES[country]);

  document.querySelectorAll('[data-vf-search-action]').forEach((searchAction) => {
    searchAction.classList.toggle('vf-search-country-hidden', !hasCountry);
    searchAction.dataset.vfSearchCountry = hasCountry ? country : '';
  });
}


function escapeSearchTag(value) {
  return String(value || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function buildCountrySearchQuery(term, country = getActiveCountry()) {
  const cleanTerm = cleanCountrySearchQuery(term).trim();
  const tag = COUNTRIES[country]?.tag;

  if (!cleanTerm || !tag) return cleanTerm;

  return `${cleanTerm} tag:"${escapeSearchTag(tag)}"`;
}

function cleanCountrySearchQuery(query) {
  let cleanQuery = String(query || '').trim();

  Object.values(COUNTRIES).forEach(({ tag }) => {
    const escapedTag = tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const suffix = new RegExp(`\\s+tag:"${escapedTag}"\\s*$`, 'i');
    cleanQuery = cleanQuery.replace(suffix, '');
  });

  return cleanQuery.trim();
}

function cleanVisibleSearchInputs() {
  document.querySelectorAll('input[type="search"][name="q"]').forEach((input) => {
    const cleanValue = cleanCountrySearchQuery(input.value);
    if (cleanValue !== input.value) input.value = cleanValue;
  });
}

function prepareStandaloneSearchForm(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (form.matches('.predictive-search-form')) return;

  const action = new URL(form.action || window.location.href, window.location.origin);
  if (!action.pathname.endsWith('/search')) return;

  const input = form.querySelector('input[name="q"]');
  if (!(input instanceof HTMLInputElement)) return;

  const country = getActiveCountry();
  if (!country || !COUNTRIES[country]) {
    event.preventDefault();
    document.getElementById('vf-header-country-trigger')?.click();
    return;
  }

  input.value = buildCountrySearchQuery(input.value, country);
}

function getHeaderLogoLinks() {
  const selectors = [
    '#header-component .header-logo a',
    '#header-component a.header-logo',
    '#header-component .header__logo a',
    '#header-component a.header__logo-link',
    '#header-component [class*="header-logo"] a',
    '#header-component a[class*="header-logo"]',
  ];

  return [...document.querySelectorAll(selectors.join(','))];
}

function updateHeaderLogoDestination(country) {
  if (!country || !COUNTRIES[country]) return;

  getHeaderLogoLinks().forEach((link) => {
    link.setAttribute('href', getCountryHomeUrl(country));
    link.setAttribute('data-vf-country-home-link', country);
  });
}

function handleCountryLogoClick(event) {
  const link = event.target.closest('#header-component a');
  if (!link) return;

  const isLogoLink =
    link.matches('.header-logo, .header__logo-link, [class*="header-logo"]') ||
    link.closest('.header-logo, .header__logo, [class*="header-logo"]') ||
    link.querySelector('.header-logo, .header__logo, [class*="header-logo"]');

  if (!isLogoLink) return;

  const activeCountry = getActiveCountry();
  if (!activeCountry || !COUNTRIES[activeCountry]) return;

  event.preventDefault();
  event.stopPropagation();
  window.location.assign(getCountryHomeUrl(activeCountry));
}

function initSelector(selector) {
  if (selector.dataset.vfInitialized === 'true') return;
  selector.dataset.vfInitialized = 'true';

  const pageCountry = selector.dataset.pageCountry || '';
  const trigger = selector.querySelector('[data-vf-country-open]');
  const modal = selector.querySelector('[data-vf-country-modal]');
  const close = selector.querySelector('[data-vf-country-close]');
  const overlay = selector.querySelector('[data-vf-country-overlay]');

  const openModal = () => {
    if (!modal) return;
    modal.hidden = false;
    trigger?.setAttribute('aria-expanded', 'true');
    document.documentElement.style.overflow = 'hidden';
  };

  const closeModal = () => {
    if (!modal) return;
    modal.hidden = true;
    trigger?.setAttribute('aria-expanded', 'false');
    document.documentElement.style.overflow = '';
  };

  trigger?.addEventListener('click', openModal);
  close?.addEventListener('click', closeModal);
  overlay?.addEventListener('click', closeModal);
  selector.addEventListener('vf:open-country-selector', openModal);

  selector.querySelectorAll('[data-vf-country-choice]').forEach((choice) => {
  choice.addEventListener('click', async (event) => {
    event.preventDefault();

    if (choice.dataset.vfNavigating === 'true') return;

    const country = choice.dataset.vfCountryChoice;

    if (!country || !COUNTRIES[country]) return;

    /*
     * Si esta página pertenece a un Grupo SEO regional, usamos la referencia
     * exacta del producto/colección equivalente. Si no existe contexto regional,
     * conservamos el comportamiento anterior y enviamos al home del país.
     */
    const regionalDestination = getRegionalDestination(selector, country);
    const destination = regionalDestination || getCountryHomeUrl(country);

    if (!destination) return;

    choice.dataset.vfNavigating = 'true';

    /*
     * Primero guardamos completamente el nuevo país.
     * Solo después navegamos al equivalente regional o, como fallback, al home.
     */
    try {
      await saveCountry(country);
    } finally {
      window.location.assign(destination);
    }
  });
});

  let activeCountry = pageCountry && COUNTRIES[pageCountry] ? pageCountry : readSavedCountry();

  if (pageCountry && COUNTRIES[pageCountry]) {
    saveCountry(pageCountry);
    activeCountry = pageCountry;
  } else if (activeCountry) {
    // Sincroniza selecciones antiguas de localStorage con Liquid mediante el carrito.
    saveCountry(activeCountry);
  } else {
    window.VerdifloraCountryReady = Promise.resolve();
  }

  updateSearchVisibility(activeCountry);

  if (activeCountry) {
    updateHeaderLogoDestination(activeCountry);
  }
}

function initAllSelectors() {
  document.querySelectorAll('verdiflora-country-selector').forEach(initSelector);

  const activeCountry = getActiveCountry();
  updateSearchVisibility(activeCountry);

  if (activeCountry) {
    updateHeaderLogoDestination(activeCountry);
  }
}

function requireCountryBeforeMenuNavigation(event) {
  const link = event.target.closest('[data-vf-country-required-menu] a');
  if (!link) return;

  const selector = document.querySelector('verdiflora-country-selector');
  if (!selector) return;

  event.preventDefault();
  event.stopPropagation();
  selector.dispatchEvent(new CustomEvent('vf:open-country-selector'));
}

function openCountrySelectorFromCustomLink(event) {
  const link = event.target.closest('a[href="#seleccionar-pais"]');
  if (!link) return;

  event.preventDefault();
  document.getElementById('vf-header-country-trigger')?.click();
}

window.VerdifloraCountryReady = countrySyncPromise;
window.VerdifloraGetActiveCountry = getActiveCountry;
window.VerdifloraBuildSearchQuery = buildCountrySearchQuery;
window.VerdifloraCleanSearchQuery = cleanCountrySearchQuery;

document.addEventListener('click', handleCountryLogoClick, true);
document.addEventListener('click', requireCountryBeforeMenuNavigation, true);
document.addEventListener('click', openCountrySelectorFromCustomLink);
document.addEventListener('submit', prepareStandaloneSearchForm, true);
document.addEventListener('DOMContentLoaded', () => {
  initAllSelectors();
  cleanVisibleSearchInputs();
});
document.addEventListener('shopify:section:load', () => {
  initAllSelectors();
  cleanVisibleSearchInputs();
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;

  const selector = document.querySelector('verdiflora-country-selector');
  const modal = selector?.querySelector('[data-vf-country-modal]:not([hidden])');
  if (!modal) return;

  modal.hidden = true;
  selector.querySelector('[data-vf-country-open]')?.setAttribute('aria-expanded', 'false');
  document.documentElement.style.overflow = '';
});
